import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { API_BASE_URL } from "../apiConfig";

const appApi = createApi({
  reducerPath: "appApi",
  baseQuery: fetchBaseQuery({
    baseUrl: API_BASE_URL,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem("userToken");
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
      return headers;
    },
  }),

  // Tag types used for automatic cache invalidation
  tagTypes: ["Notification"],

  endpoints: (builder) => ({
    // ── Signup Flow ────────────────────────────────────────────────────────
    registerUser: builder.mutation({
      query: (user) => ({
        url: "/api/auth/register",
        method: "POST",
        body: user,
      }),
    }),

    verifyOtp: builder.mutation({
      query: (payload) => ({
        url: "/api/auth/verify-otp",
        method: "POST",
        body: payload,
      }),
    }),

    resendOtp: builder.mutation({
      query: (payload) => ({
        url: "/api/auth/resend-otp",
        method: "POST",
        body: payload,
      }),
    }),

    // ── Password Reset Flow ───────────────────────────────────────────────
    requestPasswordReset: builder.mutation({
      query: (payload) => ({
        url: "/api/auth/request-reset",
        method: "POST",
        body: payload,
      }),
    }),

    verifyResetOtp: builder.mutation({
      query: (payload) => ({
        url: "/api/auth/verify-reset-otp",
        method: "POST",
        body: payload,
      }),
    }),

    resetPassword: builder.mutation({
      query: (payload) => ({
        url: "/api/auth/reset-password",
        method: "POST",
        body: payload,
      }),
    }),

    // ── Login / Logout ────────────────────────────────────────────────────
    loginUser: builder.mutation({
      query: (user) => ({
        url: "/users/login",
        method: "POST",
        body: user,
      }),
    }),

    logoutUser: builder.mutation({
      query: (payload) => ({
        url: "/logout",
        method: "DELETE",
        body: payload,
      }),
    }),

    deleteAccount: builder.mutation({
      query: (payload) => ({
        url: "/users/delete-account",
        method: "DELETE",
        body: payload,
      }),
    }),

    // ── Notifications ─────────────────────────────────────────────────────

    // Fetch paginated notifications feed
    getNotifications: builder.query({
      query: (page = 1) => `/api/notifications?page=${page}&limit=20`,
      providesTags: (result) =>
        result
          ? [
              ...result.notifications.map(({ _id }) => ({ type: "Notification", id: _id })),
              { type: "Notification", id: "LIST" },
            ]
          : [{ type: "Notification", id: "LIST" }],
    }),

    // Mark a single notification as seen
    markNotificationAsSeen: builder.mutation({
      query: (notificationId) => ({
        url: `/api/notifications/${notificationId}/seen`,
        method: "PUT",
      }),
      invalidatesTags: [{ type: "Notification", id: "LIST" }],
    }),

    // Get unread notification count (for the navbar badge)
    getUnreadCount: builder.query({
      query: () => `/api/notifications/unread-count`,
      providesTags: [{ type: "Notification", id: "UNREAD_COUNT" }],
    }),

    // Mark all notifications as read (called when the page opens)
    markAllNotificationsAsRead: builder.mutation({
      query: () => ({
        url: `/api/notifications/mark-all-read`,
        method: "PUT",
      }),
      invalidatesTags: [
        { type: "Notification", id: "LIST" },
        { type: "Notification", id: "UNREAD_COUNT" },
      ],
    }),
  }),
});

export const {
  useRegisterUserMutation,
  useVerifyOtpMutation,
  useResendOtpMutation,
  useRequestPasswordResetMutation,
  useVerifyResetOtpMutation,
  useResetPasswordMutation,
  useLoginUserMutation,
  useLogoutUserMutation,
  useDeleteAccountMutation,
  // Notification hooks
  useGetNotificationsQuery,
  useMarkNotificationAsSeenMutation,
  useGetUnreadCountQuery,
  useMarkAllNotificationsAsReadMutation,
} = appApi;

export default appApi;
